<template>
    <div v-if="show" class="fixed inset-0 overflow-y-auto" style="z-index: 9999 !important;" @click.self="close">
        <div class="flex min-h-screen items-center justify-center p-4">
            <!-- Backdrop -->
            <div class="foxy-modal-overlay transition-opacity"></div>
            
            <!-- Modal -->
            <div class="foxy-modal relative max-w-md max-h-[90vh] overflow-hidden !border-0" style="z-index: 10000 !important;">
                <!-- Header -->
                <div class="foxy-modal-section flex items-center justify-between p-6 border-b">
                    <h3 class="text-lg font-semibold text-foxy-text">
                        Поскаржитися на контент
                    </h3>
                    <button @click="close" 
                            class="foxy-icon-button !h-7 !w-auto !rounded-none">
                        <i class="fas fa-times text-xl"></i>
                    </button>
                </div>

                <!-- Content -->
                <div class="p-6 space-y-4">
                    <!-- Content Preview -->
                    <div class="bg-foxy-elevated rounded-lg p-4">
                        <h4 class="text-sm font-medium text-foxy-text-secondary mb-2">
                            Контент:
                        </h4>
                        <p class="text-sm text-foxy-text">
                            {{ contentPreview }}
                        </p>
                    </div>

                    <!-- Report Type -->
                    <div>
                        <label class="block text-sm font-medium text-foxy-text mb-2">
                            Причина скарги *
                        </label>
                        <select v-model="reportType" 
                                class="foxy-select !px-3 !py-2 !rounded-lg">
                            <option value="">Оберіть причину скарги</option>
                            <option v-for="(label, value) in reportTypes" :key="value" :value="value">
                                {{ label }}
                            </option>
                        </select>
                    </div>

                    <!-- Reason -->
                    <div>
                        <label class="block text-sm font-medium text-foxy-text mb-2">
                            Додаткова інформація
                        </label>
                        <textarea v-model="reason" 
                                  rows="4" 
                                  placeholder="Опишіть причину скарги (необов'язково)"
                                  class="foxy-textarea !px-3 !py-2 !rounded-lg !resize-none">
                        </textarea>
                        <p class="mt-2 text-xs text-foxy-text-secondary">
                            Скарги розглядаються модераторами. Подання безпідставних скарг може призвести до обмежень.
                        </p>
                    </div>

                    <!-- Error Message -->
                    <div v-if="errorMessage" class="bg-foxy-error/10 border border-foxy-error/30 rounded-lg p-3">
                        <p class="text-sm text-foxy-error">{{ errorMessage }}</p>
                    </div>

                    <!-- Success Message -->
                    <div v-if="successMessage" class="bg-foxy-success/10 border border-foxy-success/30 rounded-lg p-3">
                        <p class="text-sm text-foxy-success">{{ successMessage }}</p>
                    </div>
                </div>

                <!-- Footer -->
                <div class="foxy-modal-section flex items-center justify-end space-x-3 p-6 border-t">
                    <button @click="close" 
                            class="foxy-btn-quiet !px-4 !py-2 !text-base !font-normal">
                        Скасувати
                    </button>
                    <button @click="submitReport" 
                            :disabled="!reportType || isSubmitting"
                            class="foxy-btn-destructive !px-6 !py-2 !text-base !font-medium !rounded-lg disabled:cursor-not-allowed">
                        {{ isSubmitting ? 'Надсилаємо…' : 'Надіслати скаргу' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';

export default {
    name: 'ReportModal',
    props: {
        show: {
            type: Boolean,
            default: false
        },
        reportableType: {
            type: String,
            required: true
        },
        reportableId: {
            type: [Number, String],
            required: true
        },
        contentPreview: {
            type: String,
            default: ''
        },
        contentUrl: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            reportType: '',
            reason: '',
            reportTypes: {},
            isSubmitting: false,
            errorMessage: '',
            successMessage: ''
        };
    },
    mounted() {
        this.loadReportTypes();
    },
    methods: {
        async loadReportTypes() {
            try {
                const response = await axios.get('/api/reports/types');
                this.reportTypes = response.data.types;
            } catch (error) {
                console.error('Error loading report types:', error);
                // Fallback types
                this.reportTypes = {
                    'spam': 'Спам',
                    'harassment': 'Образи, булінг, переслідування',
                    'inappropriate': 'Образливий або неприйнятний контент',
                    'copyright': 'Порушення авторського права',
                    'fake': 'Неправдива або оманлива інформація',
                    'hate_speech': 'Розпалювання ненависті',
                    'violence': 'Жорстокий або шокуючий контент',
                    'adult_content': 'Контент для дорослих (18+)',
                    'other': 'Інше'
                };
            }
        },

        async submitReport() {
            if (!this.reportType) {
                this.errorMessage = 'Будь ласка, виберіть причину скарги';
                return;
            }

            this.isSubmitting = true;
            this.errorMessage = '';
            this.successMessage = '';

            try {
                const response = await axios.post('/api/reports', {
                    reportable_type: this.reportableType,
                    reportable_id: this.reportableId,
                    type: this.reportType,
                    reason: this.reason,
                    content_url: this.contentUrl
                });

                if (response.data.success) {
                    this.successMessage = response.data.message;
                    
                    // Закрываем модальное окно через 2 секунды
                    setTimeout(() => {
                        this.close();
                        this.resetForm();
                    }, 2000);
                } else {
                    this.errorMessage = response.data.message || 'Помилка при відправці скарги';
                }
            } catch (error) {
                console.error('Error submitting report:', error);
                
                if (error.response && error.response.data && error.response.data.message) {
                    this.errorMessage = error.response.data.message;
                } else {
                    this.errorMessage = 'Помилка при відправці скарги. Спробуйте ще раз.';
                }
            } finally {
                this.isSubmitting = false;
            }
        },

        close() {
            this.$emit('close');
        },

        resetForm() {
            this.reportType = '';
            this.reason = '';
            this.errorMessage = '';
            this.successMessage = '';
            this.isSubmitting = false;
        }
    },

    watch: {
        show(newVal) {
            if (!newVal) {
                // Сбрасываем форму при закрытии
                this.resetForm();
            }
        }
    }
};
</script>

<style scoped>
/* Дополнительные стили при необходимости */
</style>

<style>
/* Глобальные стили для модального окна жалоб */
.report-modal-overlay {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    z-index: 9999 !important;
    background-color: rgba(0, 0, 0, 0.5);
}

.report-modal-content {
    z-index: 10000 !important;
}
</style>
